import { useRef, useState } from "react";
import { useResponsive } from "../../hooks/customUseResponsive";
import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { leaveLogSchema as taskboardSchema } from "../../schemas/leaveLog";
import { Box, Stack, Typography } from "@mui/material";
import DatePickerComponent from "../DatePickerComp/DatePickerComp";
import { LoadingButton } from "@mui/lab";
import Loader from "../Loader";
import DataGridComp from "../DataGrid/DataGrid";
import styles from "./styles.module.css";
import TaskboardLeaveQuery from "../../utils/reactQuery/leave/TaskboardLeave";
import ConfirmModal from "../ConfirmationModal/ConfirmModal";
import { useMutation } from "@tanstack/react-query";
import { postRejectTaskboardLeave } from "../../services/leave/apiRejectTaskboardLeave";
import rejectIcon from "../../styles/icons/crossIcon.svg";
import { formatDate, isAdmin } from "../../utils/helpers";
import toast from "react-hot-toast";

export default function Taskboard({ currentEmployeeId }) {
    const date = new Date();
    const isMobile = useResponsive("down", "sm");
    const [isGenerating, setIsGenerating] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const startDate = useRef(formatDate(new Date(date.getFullYear(), date.getMonth(), 1)));
    const endDate = useRef(formatDate(date));

    const { report, isLoading, refetch } = TaskboardLeaveQuery(
        startDate.current,
        endDate.current,
        currentEmployeeId
    );

    const { mutate: mutateRejectTaskboardLeave } =
        useMutation({
            mutationFn: postRejectTaskboardLeave,
            onSuccess: (data) => {
                toast.success(data.data.message || "Taskboard leave rejected");
                refetch();
            },
            onError: (data) => {
                console.error("error rejecting taskboard leave", data);
                toast.error(data.response?.data?.error || "Failed to reject request");
            },
            onSettled: () => {
                setRejectModal(false);
            },
        });

    const handleReject = (applicationId) => {
        setSelectedId(applicationId);
        setRejectModal(true);
    };

    const confirmReject = () => {
        mutateRejectTaskboardLeave({ id: selectedId });
    };

    const columns = [
        {
            field: "col1",
            headerName: "Employee Name",
            minWidth: isMobile ? 150 : 200,
            flex: 1,
            sortable: false,
            resizable: false,
            renderCell: (value) => <span>{value.value}</span>,
        },
        {
            field: "col2",
            headerName: "Date",
            minWidth: isMobile ? 150 : 200,
            flex: 1,
            sortable: false,
            resizable: false,
            renderCell: (value) => <span>{value.value}</span>,
        },
        {
            field: "col3",
            headerName: "Status",
            minWidth: isMobile ? 150 : 200,
            flex: 1,
            sortable: false,
            resizable: false,
            renderCell: (value) => (
                <Stack className={styles.statusCont}>
                    <Typography className={value.value == "Approved" ? styles.statusApproved : styles.statusRejected}>
                        {value.value}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: "col4",
            headerName: "Action",
            minWidth: isMobile ? 150 : 200,
            flex: 0.8,
            sortable: false,
            resizable: false,
            renderCell: (value) =>
                isAdmin() && value.row.col3 !== "Rejected" ? (
                    <Stack className={styles.iconsCont}>
                        <img
                            className={styles.icon}
                            src={rejectIcon}
                            onClick={() => handleReject(value.value)}
                            alt="reject"
                        />
                    </Stack>
                ) : null,
        },
    ];

    const defaultValues = {
        startDate: dayjs(startDate.current),
        endDate: dayjs(endDate.current),
    };

    const methods = useForm({
        resolver: yupResolver(taskboardSchema),
        defaultValues,
    });

    const onSubmit = async (formData) => {
        setIsGenerating(true);

        const start_date = formatDate(formData.startDate);
        const end_date = formatDate(formData.endDate);

        startDate.current = start_date;
        endDate.current = end_date;

        await refetch();
        setIsGenerating(false);
    };

    return (
        <Box>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Stack direction="row" className={styles.formContainer}>
                        <Stack className={styles.dateSection}>
                            <Typography className={styles.datePickerlabel}>
                                From Date
                            </Typography>

                            <DatePickerComponent name="startDate" />
                        </Stack>

                        <Stack className={styles.dateSection}>
                            <Typography className={styles.datePickerlabel}>
                                To Date
                            </Typography>
                            <DatePickerComponent name="endDate" />
                        </Stack>

                        <LoadingButton
                            loading={isGenerating}
                            type="submit"
                            variant="contained"
                            className={styles.generateBtn}
                        >
                            Generate Report
                        </LoadingButton>
                    </Stack>
                </form>
            </FormProvider>
            <Box className={styles.dataGridContainer}>
                {isLoading && (
                    <Stack className={styles.loaderCont}>
                        <Loader />
                    </Stack>
                )}
                <DataGridComp
                    columns={columns || []}
                    initialRows={report || []}
                    footer={false}
                    pageSize={50}
                />
            </Box>
            {rejectModal && (
                <ConfirmModal
                    openProp={rejectModal}
                    handleClose={() => setRejectModal(false)}
                    handleConfirm={confirmReject}
                    tabHeader="Reject Taskboard Leave"
                    content="Do you want to reject this taskboard leave?"
                    btnText="Reject"
                />
            )}
        </Box>
    );
}
